import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { DailyPickSetAdminDto } from '@lunary/api-client'
import { pickFaceFor, useDeck } from '@lunary/ui-web/deck'
import { api, ApiError } from '../../shared/api/client'
import { CardFace } from '../../shared/ui/card-face'
import styles from '../../shared/ui/expert.module.css'
import { moscowToday } from '../../shared/lib/moscow-date'
import { OptionEditor } from './option-editor'

export const DAILY_PICKS_KEY = ['daily-picks']

export function DailyPicksEditor({ entry, readOnly: past }: { entry: DailyPickSetAdminDto; readOnly: boolean }) {
  const qc = useQueryClient()
  const deck = useDeck()
  const [position, setPosition] = useState(entry.options[0]?.position ?? 1)
  const published = entry.status === 'published'
  const readOnly = past || !entry.editable
  const future = entry.date > moscowToday()
  const taken = entry.options.map((o) => o.card.id)
  const complete = entry.options.length === 3 && entry.options.every((o) => o.interpretation.trim().length > 0)
  const current = entry.options.find((o) => o.position === position) ?? entry.options[0]

  const publish = useMutation({
    mutationFn: () => api.post<DailyPickSetAdminDto>(`/admin/daily-picks/${entry.date}/publish`, undefined),
    onSuccess: () => void qc.invalidateQueries({ queryKey: DAILY_PICKS_KEY }),
  })

  return (
    <>
      {published && !readOnly && (
        <p className={styles.hint}>
          {future
            ? 'Опубликовано на будущий день: тексты и карты ещё можно менять.'
            : 'Опубликовано: тексты и карты можно менять до конца дня, правку сразу увидят все. Прошедшие дни менять нельзя.'}
        </p>
      )}
      <div className={styles.trio} role="group" aria-label="Карты набора">
        {entry.options.map((o) => {
          const ready = o.interpretation.trim().length > 0
          return (
            <button key={o.position} type="button" className={styles.option} aria-pressed={o.position === current?.position} onClick={() => setPosition(o.position)}>
              <CardFace src={pickFaceFor(deck, o.card)} number={o.card.arcana === 'major' ? o.card.number : null} name={o.card.name} size="sm" />
              <span className={styles.optionTitle}>
                <span className={styles.historyDate}>Карта {o.position}</span>
                <span className={styles.cardName}>{o.card.name}</span>
                <span className={ready ? styles.statusPublished : styles.status}>{ready ? 'текст есть' : 'без текста'}</span>
              </span>
            </button>
          )
        })}
      </div>
      {current && (
        <OptionEditor
          key={`${current.position}:${current.card.id}`}
          date={entry.date}
          option={current}
          readOnly={readOnly}
          published={published}
          canSwap={!readOnly}
          taken={taken}
        />
      )}
      {!published && !readOnly && (
        <div className={styles.publishBar}>
          <button type="button" disabled={publish.isPending || !complete} onClick={() => publish.mutate()}>
            {publish.isPending ? 'Публикую…' : 'Опубликовать три карты'}
          </button>
          <span className={styles.hint}>{complete ? 'У всех трёх карт есть описание, набор готов к публикации.' : 'Нужно описание у всех трёх карт.'}</span>
        </div>
      )}
      {publish.error && (
        <p className="error-box">{publish.error instanceof ApiError || publish.error instanceof Error ? publish.error.message : 'Что-то пошло не так'}</p>
      )}
    </>
  )
}
